import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './ClientDetailView.css';
import './ClientDetailSubHeader.css';

function ClientDetailView() {
  const { clientId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [connection, setConnection] = useState(null);
  const [stats, setStats] = useState(null);
  const [journal, setJournal] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Modal states
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  // Form states
  const [recForm, setRecForm] = useState({ frequency_id: '', title: '', description: '' });
  const [noteForm, setNoteForm] = useState({ content: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };


  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load client info from my-clients
      const clients = await apiClient.getMyClients();
      const foundClient = clients.find(c => c.client_user_id === clientId);
      
      if (!foundClient) {
        navigate('/practitioner');
        return;
      }
      
      setClient(foundClient);
      setConnection(foundClient);
      
      // Load data based on active tab
      if (activeTab === 'stats' && foundClient.permissions.view_statistics) {
        const statsData = await apiClient.getClientStats(clientId);
        setStats(statsData);
      } else if (activeTab === 'journal' && foundClient.permissions.view_journal) {
        const journalData = await apiClient.getClientJournal(clientId);
        setJournal(journalData);
      } else if (activeTab === 'recommendations' && foundClient.permissions.recommend_frequencies) {
        const [recsData, freqData] = await Promise.all([
          apiClient.getClientRecommendations(clientId),
          apiClient.getFrequencies()
        ]);
        setRecommendations(recsData);
        setFrequencies(freqData);
      } else if (activeTab === 'notes' && foundClient.permissions.write_notes) {
        const notesData = await apiClient.getClientSessionNotes(clientId);
        setSessionNotes(notesData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecommendation = async () => {
    if (!recForm.frequency_id || !recForm.title) {
      setError('Bitte Frequenz und Titel ausfüllen');
      return;
    }

    try {
      await apiClient.createFrequencyRecommendation(clientId, recForm.frequency_id, recForm.title, recForm.description);
      setShowRecommendationModal(false);
      setRecForm({ frequency_id: '', title: '', description: '' });
      setSuccess('Empfehlung erfolgreich erstellt!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (error) {
      setError(error.message || 'Fehler beim Erstellen der Empfehlung');
    }
  };

  const handleDeleteRecommendation = async (recId) => {
    if (!window.confirm('Möchten Sie diese Empfehlung wirklich löschen?')) {
      return;
    }

    try {
      await apiClient.deletePractitionerRecommendation(recId);
      loadData();
    } catch (error) {
      setError('Fehler beim Löschen der Empfehlung');
    }
  };

  const handleCreateOrUpdateNote = async () => {
    if (!noteForm.content.trim()) {
      setError('Bitte Notizinhalt eingeben');
      return;
    }

    try {
      if (editingNote) {
        await apiClient.updateSessionNote(editingNote.id, noteForm.content);
        setSuccess('Notiz aktualisiert!');
      } else {
        await apiClient.createSessionNote(clientId, noteForm.content, new Date().toISOString());
        setSuccess('Notiz erstellt!');
      }
      
      setShowNoteModal(false);
      setEditingNote(null);
      setNoteForm({ content: '' });
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (error) {
      setError(error.message || 'Fehler beim Speichern der Notiz');
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ content: note.note_content });
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Möchten Sie diese Notiz wirklich löschen?')) {
      return;
    }

    try {
      await apiClient.deleteSessionNote(noteId);
      loadData();
    } catch (error) {
      setError('Fehler beim Löschen der Notiz');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Lade Daten...</p>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="client-detail-view">
        {/* Sub-Header with Back Button and Client Name */}
        <div className="client-detail-subheader">
          <button onClick={() => navigate('/practitioner')} className="btn-back-client">
            ← Zurück zu Begleitungen
          </button>
          <h2 className="client-detail-title">Begleitung: {client.client_name}</h2>
        </div>
        
        <main className="client-detail-main">
        {success && (
          <div className="success-message">{success}</div>
        )}
        
        {error && (
          <div className="error-message">{error}</div>
        )}

        {/* Client Info Card */}
        <div className="client-info-card">
          <div className="client-avatar-large">
            {client.client_name.charAt(0).toUpperCase()}
          </div>
          <div className="client-info-details">
            <h2>{client.client_name}</h2>
            <p className="client-zen-id">{client.client_zen_id}</p>
            <p className="client-email">{client.client_email}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          {connection.permissions.view_statistics && (
            <button
              className={`detail-tab ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              📊 Statistiken
            </button>
          )}
          {connection.permissions.view_journal && (
            <button
              className={`detail-tab ${activeTab === 'journal' ? 'active' : ''}`}
              onClick={() => setActiveTab('journal')}
            >
              📖 Journal
            </button>
          )}
          {connection.permissions.recommend_frequencies && (
            <button
              className={`detail-tab ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommendations')}
            >
              ✨ Empfehlungen
            </button>
          )}
          {connection.permissions.write_notes && (
            <button
              className={`detail-tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              📝 Begleitungs-Notizen
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'stats' && connection.permissions.view_statistics && (
          <div className="tab-content">
            <h3>Statistiken</h3>
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">🔢</div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.total_activations}</div>
                    <div className="stat-label">Gesamt-Aktivierungen</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.this_week}</div>
                    <div className="stat-label">Diese Woche</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📆</div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.this_month}</div>
                    <div className="stat-label">Dieser Monat</div>
                  </div>
                </div>
              </div>
            )}

            {stats && stats.most_used && stats.most_used.length > 0 && (
              <div className="most-used-section">
                <h4>Meist genutzte Frequenzen</h4>
                <div className="frequency-list">
                  {stats.most_used.map((freq, index) => (
                    <div key={freq._id} className="frequency-item">
                      <span className="freq-rank">#{index + 1}</span>
                      <span className="freq-icon">{freq.frequency_icon}</span>
                      <span className="freq-name">{freq.frequency_name}</span>
                      <span className="freq-count">{freq.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'journal' && connection.permissions.view_journal && (
          <div className="tab-content">
            <h3>Journal-Einträge</h3>
            {journal.length === 0 ? (
              <div className="empty-state">
                <p>Noch keine Journal-Einträge vorhanden.</p>
              </div>
            ) : (
              <div className="journal-list">
                {journal.map((entry) => (
                  <div key={entry.id} className="journal-entry">
                    <div className="journal-header">
                      <span className="journal-frequency">{entry.frequency_icon} {entry.frequency_name}</span>
                      <span className="journal-date">{new Date(entry.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                    <div className="journal-content">{entry.content}</div>
                    {entry.mood && (
                      <div className="journal-mood">Stimmung: {entry.mood}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'recommendations' && connection.permissions.recommend_frequencies && (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Frequenz-Empfehlungen</h3>
              <button onClick={() => setShowRecommendationModal(true)} className="btn-add">
                + Neue Empfehlung
              </button>
            </div>
            
            {recommendations.length === 0 ? (
              <div className="empty-state">
                <p>Noch keine Empfehlungen erstellt.</p>
              </div>
            ) : (
              <div className="recommendations-list">
                {recommendations.map((rec) => (
                  <div key={rec.id} className="recommendation-card" style={{ borderLeftColor: rec.frequency_color }}>
                    <div className="rec-header">
                      <span className="rec-frequency">{rec.frequency_icon} {rec.frequency_name}</span>
                      <span className={`rec-status ${rec.status}`}>{rec.status === 'active' ? 'Aktiv' : rec.status === 'completed' ? 'Erledigt' : 'Verworfen'}</span>
                    </div>
                    <h4>{rec.title}</h4>
                    <p>{rec.description}</p>
                    <div className="rec-footer">
                      <span className="rec-date">{new Date(rec.created_at).toLocaleDateString('de-DE')}</span>
                      <button onClick={() => handleDeleteRecommendation(rec.id)} className="btn-delete-small">
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && connection.permissions.write_notes && (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Begleitungs-Notizen</h3>
              <button onClick={() => { setEditingNote(null); setNoteForm({ content: '' }); setShowNoteModal(true); }} className="btn-add">
                + Neue Notiz
              </button>
            </div>
            
            {sessionNotes.length === 0 ? (
              <div className="empty-state">
                <p>Noch keine Notizen erstellt.</p>
              </div>
            ) : (
              <div className="notes-list">
                {sessionNotes.map((note) => (
                  <div key={note.id} className="note-card">
                    <div className="note-header">
                      <span className="note-date">{new Date(note.date).toLocaleDateString('de-DE')}</span>
                      <div className="note-actions">
                        <button onClick={() => handleEditNote(note)} className="btn-edit-small">✏️</button>
                        <button onClick={() => handleDeleteNote(note.id)} className="btn-delete-small">🗑️</button>
                      </div>
                    </div>
                    <div className="note-content">{note.note_content}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recommendation Modal */}
        {showRecommendationModal && (
          <div className="modal-overlay" onClick={() => setShowRecommendationModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Neue Frequenz-Empfehlung</h2>
                <button onClick={() => setShowRecommendationModal(false)} className="modal-close">×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Frequenz</label>
                  <select
                    value={recForm.frequency_id}
                    onChange={(e) => setRecForm({ ...recForm, frequency_id: e.target.value })}
                  >
                    <option value="">-- Frequenz wählen --</option>
                    {frequencies.map((freq) => (
                      <option key={freq.id} value={freq.id}>
                        {freq.icon} {freq.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Titel</label>
                  <input
                    type="text"
                    value={recForm.title}
                    onChange={(e) => setRecForm({ ...recForm, title: e.target.value })}
                    placeholder="z.B. Für mehr Entspannung"
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={recForm.description}
                    onChange={(e) => setRecForm({ ...recForm, description: e.target.value })}
                    placeholder="Warum empfiehlst du diese Frequenz?"
                    rows="4"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowRecommendationModal(false)} className="btn-cancel">Abbrechen</button>
                <button onClick={handleCreateRecommendation} className="btn-submit">Empfehlung erstellen</button>
              </div>
            </div>
          </div>
        )}

        {/* Note Modal */}
        {showNoteModal && (
          <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}</h2>
                <button onClick={() => setShowNoteModal(false)} className="modal-close">×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Notiz</label>
                  <textarea
                    value={noteForm.content}
                    onChange={(e) => setNoteForm({ content: e.target.value })}
                    placeholder="Deine Beobachtungen und Notizen..."
                    rows="8"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowNoteModal(false)} className="btn-cancel">Abbrechen</button>
                <button onClick={handleCreateOrUpdateNote} className="btn-submit">Speichern</button>
              </div>
            </div>
          </div>
        )}
      </main>
      </div>
    </>
  );
}

export default ClientDetailView;
